import React, { useCallback } from 'react';
import { Box } from '@cp/ds/src/components/box';
import { Button } from '@cp/ds/src/components/button';
import { Typography } from '@cp/ds/src/components/typography';
import { Container } from '@cp/ds/src/components/container';
import { MOBILE_BREAKPOINT_THRESHOLD } from '@cp/ds/src/theme';
import loadable from '@loadable/component';
import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { Stack } from '@cp/ds/src/components/stack';
import { useMediaQuery } from '@mui/material';
import { styled } from '@cp/ds/src/theme/styled';
import { useCurrentUser } from '@cp/entities/currentUser';
import { UserType } from '@cp/entities/user';
import { Image as Img } from '@youhelp-ru/ui';
import { useRouter } from '@cp/shared/router';
import { useClientWebResolver } from '@cp/entities/clientWebRoutes';
import { ClientType } from '@common/model/client/clientType';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { CareerId } from '@common/const/catalog/career';
// import { ArrowRightIcon } from '@cp/ds/src/icons';
import { HeaderFeature } from 'src/widgets/header/model';
import { LoginButton /* , SpecialistRegistrationButton */ } from 'src/features/auth';
import VectorHorizontal from 'src/assets/shapes/vectors/vectorHorizontal.svg';
import VectorVertical from 'src/assets/shapes/vectors/vectorVertical.svg';
import { SelectionType, useClientSelectionService, useQuizFeature } from 'src/features/clientSelection';
import { /* EButtonName, */ useTrackEvent } from 'src/shared/tracking';
import { TBankRating } from 'src/app/routes/home/components/tBankRating';
import { createEventFactory } from '@cp/shared/tracking';
import { useCurrentCity } from '@cp/entities/location';

const Header = loadable(() => import('src/widgets/header'));

const PrimaryButton = styled(Button)(({ theme }) => ({
  borderRadius: '16px',
  textWrap: 'nowrap',
  userSelect: 'none',
  display: 'flex',
  flexGrow: 1,
  flexBasis: 0,
  minWidth: 0,
  [theme.breakpoints.up('md')]: {
    borderRadius: '24px',
  },
}));

const Image = styled(Img)({
  width: '100%',
  objectFit: 'cover',
  display: 'block',
  height: '100%',
});

export const HomeIntro: React.FC = () => {
  const track = useTrackEvent();
  const router = useRouter();
  const routeResolver = useClientWebResolver();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const isMiddleScreen = useMediaQuery(theme.breakpoints.between(MOBILE_BREAKPOINT_THRESHOLD, 'xl'));
  const currentUser = useCurrentUser();
  const { currentCity } = useCurrentCity({ allowEmptyValue: true });
  const quiz = useQuizFeature();
  const clientSelectionService = useClientSelectionService();

  const onFindSpecialistClick = useCallback(() => {
    track(createEventFactory({ subject: 'find-specialist', action: 'click' })());
    return router.navigate(
      routeResolver.resolve({
        page: ClientWebPage.Catalog,
        pageProps: { catalogType: CatalogItemType.Resume, careerId: CareerId.Nanny },
        cityAlias: currentCity?.alias,
      }),
    );
  }, [currentCity?.alias, routeResolver, router, track]);

  const onFindWorkClick = useCallback(async () => {
    track(createEventFactory({ subject: 'find-work', action: 'click' })());
    clientSelectionService.register(
      { clientType: ClientType.Specialist, catalogType: CatalogItemType.Vacancy },
      SelectionType.ClientAction,
    );
    const selections = await quiz(['careerId']);
    return selections.mapAsync(({ catalogType = CatalogItemType.Vacancy, careerId = CareerId.Nanny }) =>
      router.navigate(
        routeResolver.resolve({
          page: ClientWebPage.Catalog,
          pageProps: { catalogType, careerId },
          cityAlias: currentCity?.alias,
        }),
      ),
    );
  }, [clientSelectionService, currentCity?.alias, quiz, routeResolver, router, track]);

  const baseColor = theme.palette['gray ultra light'];

  return (
    <Box
      sx={{
        backgroundColor: baseColor,
        position: 'relative',
        overflow: 'hidden',
        minHeight: { xs: 400, [MOBILE_BREAKPOINT_THRESHOLD]: 408, lg: 570 },
      }}
    >
      <Box
        sx={{
          pt: { [MOBILE_BREAKPOINT_THRESHOLD]: 1.5 },
        }}
      >
        <Header
          containerProps={{ maxWidth: 1720 }}
          desktopFeatures={{ [HeaderFeature.LoginButton]: false, [HeaderFeature.CitySelection]: true, [HeaderFeature.Favorites]: false }}
          mobileFeatures={{
            [HeaderFeature.LoginButton]: false,
            [HeaderFeature.BurgerMenu]: false,
            [HeaderFeature.CitySelection]: true,
            [HeaderFeature.Favorites]: false,
          }}
          rightBlock={
            <Stack alignItems="center" direction="row">
              {currentUser.type === UserType.Guest && (
                <LoginButton
                  color="gray"
                  size={isMobile || isMiddleScreen ? 'small' : 'normal'}
                  sx={{ backgroundColor: theme.palette.white, borderColor: theme.palette['gray light'] }}
                  variant="secondary"
                >
                  Войти
                </LoginButton>
              )}
              {/* <SpecialistRegistrationButton
                className={EButtonName.FindJob}
                iconRight={<ArrowRightIcon />}
                size={isMiddleScreen ? 'small' : 'normal'}
                sx={{
                  backgroundColor: theme.palette.white,
                  borderColor: theme.palette['pink'],
                  ml: 1.5,
                  display: { xs: 'none', [MOBILE_BREAKPOINT_THRESHOLD]: 'inherit' },
                }}
                variant="secondary"
              >
                Найти работу
              </SpecialistRegistrationButton> */}
            </Stack>
          }
          sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}
        />
        <Container maxWidth={1720}>
          <Stack
            sx={{
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 1.5, md: 2.5 },
              minHeight: '400px',
              width: '100%',
              pt: { xs: 0, md: 1.5 },
              pb: { xs: 1.5, md: 2.5 },
              alignItems: 'stretch',
            }}
          >
            <Stack
              justifyContent="space-around"
              sx={{
                position: 'relative',
                width: { xs: '100%', md: '50%' },
                p: { xs: '16px 28px', md: '20px 20px 20px 90px' },
                backgroundColor: 'gray light',
                borderRadius: { xs: '16px', md: '24px' },
                flex: 1,
                gap: '8px',
              }}
            >
              <Stack
                sx={{
                  color: baseColor,
                  position: 'absolute',
                  bottom: 0,
                  left: { xs: 'auto', md: 0 },
                  right: { xs: '-2px', md: 'auto' },
                  transform: { xs: 'scaleX(-1)', md: 'none' },
                  width: '9.2%',
                }}
              >
                <VectorVertical />
              </Stack>

              <Typography
                component="h1"
                sx={{
                  typography: { xs: 'h2', md: 'h1', lg: 'h0' },
                  lineHeight: 1.2,
                  m: 0,
                }}
              >
                <Box component="span" sx={{ color: 'brand pink', fontSize: { xs: '30px', lg: '60px' } }}>
                  YouHelp:
                </Box>
                <br />
                проверенные и классные
                <br />
                няни и бебиситтеры
              </Typography>

              <Typography
                component="span"
                sx={{
                  color: 'gray',
                  typography: { xs: 'body', lg: 'h4' },
                  position: 'relative',
                  pl: { xs: 2, md: 4 },
                  '&::before': {
                    content: '""',
                    backgroundColor: 'brand pink',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: { xs: 8, lg: 12 },
                    height: { xs: 8, lg: 12 },
                    borderRadius: '50%',
                  },
                }}
              >
                34228 заказов с 2019 года
              </Typography>
            </Stack>

            <Stack
              direction="row"
              sx={{
                position: 'relative',
                width: { xs: '100%', md: '50%' },
                gap: { xs: 1.5, md: 2.5 },
                flex: 1,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: '50%',
                  minHeight: '100%',
                  borderRadius: { xs: '16px', md: '24px' },
                  overflow: 'hidden',
                  flexGrow: 1,
                }}
              >
                <Box
                  sx={{
                    display: { xs: 'none', md: 'block' },
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '20%',
                    color: baseColor,
                  }}
                >
                  <VectorHorizontal />
                </Box>
                <Box
                  sx={{
                    color: baseColor,
                    display: { xs: 'block', md: 'none' },
                    position: 'absolute',
                    bottom: '-8px',
                    left: '-3.5px',
                    width: '12%',
                  }}
                >
                  <VectorVertical />
                </Box>

                <Image alt="nannie" fetchPriority="high" loading="eager" src="https://cdn.youhelp.ru/img/homeIntroGirl.webp" />
              </Box>

              <Stack sx={{ m: 0, width: '50%', gap: { xs: 1.5, md: 2.5 } }}>
                <Box
                  sx={{
                    height: '50%',
                    overflow: 'hidden',
                    borderRadius: { xs: '16px', md: '24px' },
                  }}
                >
                  <Image alt="family" fetchPriority="high" loading="eager" src="https://cdn.youhelp.ru/img/homeIntroFamily.webp" />
                </Box>

                <Stack
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    height: '50%',
                    gap: { xs: 1.5, md: 2.5 },
                    fontSize: '30px',
                  }}
                >
                  <PrimaryButton
                    color="pink"
                    labelSx={{ fontSize: { xs: '18px', md: '16px', lg: '24px', xl: '30px' }, fontWeight: 'bold' }}
                    onClick={onFindSpecialistClick}
                    size="small"
                  >
                    Найти няню
                  </PrimaryButton>
                  <PrimaryButton
                    color="blue"
                    labelSx={{ fontSize: { xs: '18px', md: '16px', lg: '24px', xl: '30px' }, fontWeight: 'bold' }}
                    onClick={onFindWorkClick}
                    size="small"
                  >
                    Найти работу
                  </PrimaryButton>
                </Stack>
                <Box
                  sx={{
                    display: { xs: 'block', md: 'none' },
                    height: '50%',
                    overflow: 'hidden',
                    borderRadius: { xs: '16px', md: '24px' },
                  }}
                >
                  <Image alt="babysitter" fetchPriority="high" loading="eager" src="https://cdn.youhelp.ru/img/homeIntroBaby.webp" />
                </Box>
              </Stack>
            </Stack>
            <Stack
              direction="row"
              sx={{
                display: { xs: 'flex', md: 'none' },
                gap: 1.5,
              }}
            >
              <PrimaryButton
                color="pink"
                labelSx={{ fontSize: { xs: '18px', md: '16px', lg: '24px', xl: '30px' }, fontWeight: 'bold' }}
                onClick={onFindSpecialistClick}
                size="small"
              >
                Найти няню
              </PrimaryButton>
              <PrimaryButton
                color="blue"
                labelSx={{ fontSize: { xs: '18px', md: '16px', lg: '24px', xl: '30px' }, fontWeight: 'bold' }}
                onClick={onFindWorkClick}
                size="small"
              >
                Найти работу
              </PrimaryButton>
            </Stack>
          </Stack>
        </Container>
      </Box>
      <Container>
        <TBankRating />
      </Container>
    </Box>
  );
};
